import { useEffect, useState, useMemo, useRef } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ChevronDown, Calculator, Check, ChevronsUpDown } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui/sheet'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { cn } from '@/lib/utils'
import {
  useFuelTransactionById,
  useCreateFuelTransaction,
  useUpdateFuelTransaction,
} from '@/hooks/useFuelTransactions'
import { useLocations } from '@/hooks/useLocations'
import { useClients } from '@/hooks/useClients'
import { useAircraftList } from '@/hooks/useAircraft'
import { useLeases } from '@/hooks/useLeases'
import { useTransientStays } from '@/hooks/useTransientStays'
import type { FuelTransactionFormData } from '@/types/fuel-transactions'
import {
  FUEL_TYPES,
  FUEL_TYPE_LABELS,
  FUEL_PRICING_TYPES,
  FUEL_PRICING_TYPE_LABELS,
  FUEL_STATUSES,
  FUEL_STATUS_LABELS,
  FUEL_SOURCES,
  FUEL_SOURCE_LABELS,
  calculateFuelTotal,
  formatFuelTotal,
} from '@/types/fuel-transactions'
import { getClientDisplayName } from '@/types/clients'

// ============================================
// Form Schema
// ============================================

const fuelTransactionFormSchema = z.object({
  location_id: z.string().min(1, 'Location is required'),
  client_id: z.string().min(1, 'Client is required'),
  aircraft_id: z.string().min(1, 'Aircraft is required'),
  lease_contract_id: z.string().optional().nullable(),
  transient_stay_id: z.string().optional().nullable(),
  fuel_type: z.enum(['jet_a', 'avgas'] as const),
  quantity_gallons: z.coerce.number().positive('Quantity must be positive'),
  price_per_gallon: z.coerce.number().min(0, 'Price cannot be negative'),
  fueled_at: z.string().min(1, 'Fueling date/time is required'),
  pricing_type: z.enum(['contract', 'retail', 'contract_fuel_network', 'custom'] as const).optional().nullable(),
  status: z.enum(['pending', 'completed', 'voided', 'disputed'] as const),
  source: z.enum(['manual', 'csv_import', 'pos_sync', 'api'] as const),
  truck_id: z.string().optional().nullable(),
  fueler_name: z.string().optional().nullable(),
  meter_start: z.coerce.number().optional().nullable(),
  meter_end: z.coerce.number().optional().nullable(),
  external_reference: z.string().optional().nullable(),
  external_system: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
}).refine(
  (data) => {
    if (data.meter_start !== null && data.meter_end !== null) {
      return data.meter_end > data.meter_start
    }
    return true
  },
  {
    message: 'Meter end must be greater than meter start',
    path: ['meter_end'],
  }
)

type FuelTransactionFormValues = z.infer<typeof fuelTransactionFormSchema>

const defaultValues: FuelTransactionFormValues = {
  location_id: '',
  client_id: '',
  aircraft_id: '',
  lease_contract_id: null,
  transient_stay_id: null,
  fuel_type: 'jet_a',
  quantity_gallons: 0,
  price_per_gallon: 0,
  fueled_at: '',
  pricing_type: 'contract',
  status: 'completed',
  source: 'manual',
  truck_id: null,
  fueler_name: null,
  meter_start: null,
  meter_end: null,
  external_reference: null,
  external_system: null,
  notes: null,
}

// ============================================
// Prefill type (for opening from aircraft card)
// ============================================

export interface FuelTransactionPrefill {
  aircraftId: string
  clientId?: string | null
  leaseContractId?: string | null
  transientStayId?: string | null
  fuelPricePerGallon?: number | null
  pricingType?: 'contract' | 'retail' | 'contract_fuel_network' | 'custom' | null
}

// ============================================
// Component
// ============================================

interface FuelTransactionDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  fuelTransactionId?: string
  /** Pre-selected location ID from route context. When provided, location is locked. */
  locationId?: string | null
  /** Pre-fill values when opened from aircraft card context */
  prefill?: FuelTransactionPrefill | null
}

export function FuelTransactionDrawer({
  open,
  onOpenChange,
  fuelTransactionId,
  locationId: contextLocationId,
  prefill,
}: FuelTransactionDrawerProps) {
  const isEditing = !!fuelTransactionId
  const isLocationLocked = !!contextLocationId
  const isAircraftLocked = !!prefill?.aircraftId

  // Data fetching
  const { data: existingTransaction, isLoading: isLoadingTransaction } =
    useFuelTransactionById(fuelTransactionId)
  const { data: locations } = useLocations()
  const { data: clients } = useClients()
  const { data: aircraft } = useAircraftList()
  const { data: leases } = useLeases()
  const { data: transientStays } = useTransientStays()

  // Mutations
  const createFuelTransaction = useCreateFuelTransaction()
  const updateFuelTransaction = useUpdateFuelTransaction()

  // State
  const [showDiscardDialog, setShowDiscardDialog] = useState(false)

  // Combobox open states
  const [locationOpen, setLocationOpen] = useState(false)
  const [aircraftOpen, setAircraftOpen] = useState(false)
  const [clientOpen, setClientOpen] = useState(false)

  // Track if initial form reset has happened (to prevent cascading clears on mount)
  const initialResetDone = useRef(false)

  // Collapsible section states
  const [openSections, setOpenSections] = useState({
    fueling: true,
    operations: false,
    integration: false,
    notes: false,
  })

  // Form setup
  const form = useForm<FuelTransactionFormValues>({
    resolver: zodResolver(fuelTransactionFormSchema) as any,
    defaultValues,
  })

  const {
    formState: { isDirty },
  } = form

  // Watch for dynamic fields and calculated total
  const selectedLocationId = useWatch({ control: form.control, name: 'location_id' })
  const selectedAircraftId = useWatch({ control: form.control, name: 'aircraft_id' })
  const selectedClientId = useWatch({ control: form.control, name: 'client_id' })
  const quantityGallons = useWatch({ control: form.control, name: 'quantity_gallons' })
  const pricePerGallon = useWatch({ control: form.control, name: 'price_per_gallon' })

  // Calculate total amount
  const calculatedTotal = useMemo(() => {
    if (quantityGallons > 0 && pricePerGallon > 0) {
      return calculateFuelTotal(quantityGallons, pricePerGallon)
    }
    return 0
  }, [quantityGallons, pricePerGallon])

  // ============================================
  // Cascading filtered lists
  // ============================================

  // Filter aircraft: if client is selected, narrow to that client's aircraft
  const filteredAircraft = useMemo(() => {
    if (!aircraft) return []
    let list = aircraft
    if (selectedClientId) {
      list = list.filter(
        (a) =>
          a.owner_client_id === selectedClientId ||
          a.operator_client_id === selectedClientId
      )
    }
    return list
  }, [aircraft, selectedClientId])

  // Filter leases by location + client (LeaseTableRow has both fields)
  const filteredLeases = useMemo(() => {
    if (!leases) return []
    return leases.filter(
      (l) =>
        l.status === 'active' &&
        (!selectedLocationId || l.location_id === selectedLocationId) &&
        (!selectedClientId || l.client_id === selectedClientId)
    )
  }, [leases, selectedLocationId, selectedClientId])

  // Filter transient stays by location + client (active stays only)
  const filteredTransientStays = useMemo(() => {
    if (!transientStays) return []
    return transientStays.filter(
      (s) =>
        (s.status === 'checked_in' || s.status === 'reserved') &&
        (!selectedLocationId || s.location_id === selectedLocationId) &&
        (!selectedClientId || s.client_id === selectedClientId)
    )
  }, [transientStays, selectedLocationId, selectedClientId])

  // ============================================
  // Auto-fill effects
  // ============================================

  // When aircraft selected → auto-fill client from owner
  useEffect(() => {
    if (!initialResetDone.current) return
    // Skip cascade when prefill already provides client/lease/stay
    if (prefill?.clientId || prefill?.leaseContractId || prefill?.transientStayId) return
    if (selectedAircraftId && aircraft && !isEditing) {
      const selectedAc = aircraft.find((a) => a.id === selectedAircraftId)
      if (selectedAc?.owner_client_id) {
        form.setValue('client_id', selectedAc.owner_client_id)
      }
      // Auto-select lease if exactly one match
      if (filteredLeases.length === 1) {
        form.setValue('lease_contract_id', filteredLeases[0].id)
      } else {
        form.setValue('lease_contract_id', null)
      }
    }
  }, [selectedAircraftId, aircraft, filteredLeases, form, isEditing, prefill])

  // Auto-fill fuel type from aircraft type default, fallback to category
  useEffect(() => {
    if (!initialResetDone.current) return
    if (!selectedAircraftId || !aircraft || isEditing) return
    const selectedAc = aircraft.find((a) => a.id === selectedAircraftId)
    if (!selectedAc) return
    const fuelType = selectedAc.aircraft_type_default_fuel_type
    if (fuelType === 'jet_a' || fuelType === 'avgas') {
      form.setValue('fuel_type', fuelType)
    } else {
      // Fallback: infer from aircraft category
      const cat = selectedAc.aircraft_type_category
      const inferred = cat === 'single_engine_piston' || cat === 'multi_engine_piston'
        ? 'avgas' : 'jet_a'
      form.setValue('fuel_type', inferred)
    }
  }, [selectedAircraftId, aircraft, form, isEditing])

  // Reset form when drawer opens/closes or editing transaction changes
  useEffect(() => {
    if (open) {
      if (isEditing && existingTransaction) {
        form.reset({
          location_id: existingTransaction.location_id,
          client_id: existingTransaction.client_id,
          aircraft_id: existingTransaction.aircraft_id,
          lease_contract_id: existingTransaction.lease_contract_id,
          transient_stay_id: existingTransaction.transient_stay_id,
          fuel_type: existingTransaction.fuel_type,
          quantity_gallons: existingTransaction.quantity_gallons,
          price_per_gallon: existingTransaction.price_per_gallon,
          fueled_at: formatDateTimeLocal(existingTransaction.fueled_at),
          pricing_type: existingTransaction.pricing_type,
          status: existingTransaction.status,
          source: existingTransaction.source,
          truck_id: existingTransaction.truck_id,
          fueler_name: existingTransaction.fueler_name,
          meter_start: existingTransaction.meter_start,
          meter_end: existingTransaction.meter_end,
          external_reference: existingTransaction.external_reference,
          external_system: existingTransaction.external_system,
          notes: existingTransaction.notes,
        })
        initialResetDone.current = true
      } else if (!isEditing) {
        const now = new Date()
        const defaultFueledAt = formatDateTimeLocal(now.toISOString())
        form.reset({
          ...defaultValues,
          fueled_at: defaultFueledAt,
          location_id: contextLocationId || '',
          ...(prefill && {
            aircraft_id: prefill.aircraftId,
            ...(prefill.clientId && { client_id: prefill.clientId }),
            ...(prefill.leaseContractId && { lease_contract_id: prefill.leaseContractId }),
            ...(prefill.transientStayId && { transient_stay_id: prefill.transientStayId }),
            ...(prefill.fuelPricePerGallon != null && { price_per_gallon: prefill.fuelPricePerGallon }),
            ...(prefill.pricingType && { pricing_type: prefill.pricingType }),
          }),
        })
        initialResetDone.current = true
      }
    } else {
      initialResetDone.current = false
    }
  }, [open, isEditing, existingTransaction, form, contextLocationId, prefill])

  // Toggle section
  const toggleSection = (section: keyof typeof openSections) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }))
  }

  // Handle close with dirty check
  const handleClose = () => {
    if (isDirty) {
      setShowDiscardDialog(true)
    } else {
      onOpenChange(false)
    }
  }

  // Handle form submission
  const onSubmit = async (values: FuelTransactionFormValues) => {
    const formData: FuelTransactionFormData = {
      location_id: values.location_id,
      client_id: values.client_id,
      aircraft_id: values.aircraft_id,
      lease_contract_id: values.lease_contract_id || null,
      transient_stay_id: values.transient_stay_id || null,
      fuel_type: values.fuel_type,
      quantity_gallons: values.quantity_gallons,
      price_per_gallon: values.price_per_gallon ?? 0,
      fueled_at: new Date(values.fueled_at).toISOString(),
      pricing_type: values.pricing_type || null,
      status: values.status,
      source: values.source,
      truck_id: values.truck_id || null,
      fueler_name: values.fueler_name || null,
      meter_start: values.meter_start ?? null,
      meter_end: values.meter_end ?? null,
      external_reference: values.external_reference || null,
      external_system: values.external_system || null,
      notes: values.notes || null,
    }

    try {
      if (isEditing && fuelTransactionId) {
        await updateFuelTransaction.mutateAsync({
          id: fuelTransactionId,
          data: formData,
        })
      } else {
        await createFuelTransaction.mutateAsync(formData)
      }
      form.reset()
      onOpenChange(false)
    } catch {
      // Error is handled by mutation hook
    }
  }

  const isSubmitting =
    createFuelTransaction.isPending || updateFuelTransaction.isPending
  const isLoading = isEditing && isLoadingTransaction

  // ============================================
  // Display helpers for combobox triggers
  // ============================================

  const getLocationDisplay = (id: string) =>
    locations?.find((l) => l.id === id)?.name ?? 'Loading...'

  const getAircraftDisplay = (id: string) => {
    const ac = aircraft?.find((a) => a.id === id)
    if (!ac) return 'Loading...'
    const mfr = ac.aircraft_type_manufacturer || ac.manufacturer
    const mdl = ac.aircraft_type_model || ac.model
    const detail = [mfr, mdl].filter(Boolean).join(' ')
    return detail ? `${ac.tail_number} — ${detail}` : ac.tail_number
  }

  const getClientDisplay = (id: string) => {
    const client = clients?.find((c) => c.id === id)
    if (!client) return 'Loading...'
    return getClientDisplayName(client)
  }

  return (
    <>
      <Sheet open={open} onOpenChange={handleClose}>
        <SheetContent
          side="right"
          className="flex flex-col w-full sm:max-w-2xl p-0 overflow-hidden"
          onInteractOutside={(e) => {
            if (isDirty) e.preventDefault()
          }}
          onEscapeKeyDown={(e) => {
            if (isDirty) {
              e.preventDefault()
              setShowDiscardDialog(true)
            }
          }}
        >
          <SheetHeader className="px-4 py-4 border-b shrink-0">
            <SheetTitle>
              {isEditing ? 'Edit Fuel Transaction' : 'Add Fuel Transaction'}
            </SheetTitle>
            <SheetDescription>
              {isEditing
                ? 'Update the fuel transaction details below.'
                : 'Enter the fuel transaction details below.'}
            </SheetDescription>
          </SheetHeader>

          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-muted-foreground">Loading...</div>
            </div>
          ) : (
            <ScrollArea className="flex-1 min-h-0">
              <Form {...form}>
                <form
                  id="fuel-transaction-form"
                  onSubmit={form.handleSubmit(onSubmit) as any}
                  className="px-4 py-4 space-y-6"
                >
                  {/* Basic Info Section */}
                  <div className="space-y-4">
                    <h3 className="font-medium">Basic Info</h3>

                    <div className="grid grid-cols-2 gap-4">
                      {/* Aircraft Combobox (primary field) */}
                      <FormField
                        control={form.control as any}
                        name="aircraft_id"
                        render={({ field }) => (
                          <FormItem className="flex flex-col">
                            <FormLabel>Aircraft *</FormLabel>
                            <Popover open={aircraftOpen} onOpenChange={setAircraftOpen}>
                              <PopoverTrigger asChild>
                                <FormControl>
                                  <Button
                                    variant="outline"
                                    role="combobox"
                                    aria-expanded={aircraftOpen}
                                    disabled={isSubmitting || isAircraftLocked}
                                    className={cn(
                                      'w-full justify-between font-normal',
                                      !field.value && 'text-muted-foreground'
                                    )}
                                  >
                                    <span className="truncate">
                                      {field.value
                                        ? getAircraftDisplay(field.value)
                                        : 'Select aircraft...'}
                                    </span>
                                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                  </Button>
                                </FormControl>
                              </PopoverTrigger>
                              <PopoverContent className="w-[320px] p-0" align="start" onWheel={(e) => e.stopPropagation()}>
                                <Command>
                                  <CommandInput placeholder="Search tail number..." />
                                  <CommandList>
                                    <CommandEmpty>No aircraft found</CommandEmpty>
                                    <CommandGroup>
                                      {filteredAircraft.map((ac) => {
                                        const mfr = ac.aircraft_type_manufacturer || ac.manufacturer
                                        const mdl = ac.aircraft_type_model || ac.model
                                        return (
                                          <CommandItem
                                            key={ac.id}
                                            value={`${ac.tail_number} ${mfr || ''} ${mdl || ''} ${ac.aircraft_type_name || ''}`}
                                            onSelect={() => {
                                              field.onChange(ac.id)
                                              form.setValue('lease_contract_id', null)
                                              setAircraftOpen(false)
                                            }}
                                          >
                                            <Check
                                              className={cn(
                                                'mr-2 h-4 w-4 shrink-0',
                                                field.value === ac.id ? 'opacity-100' : 'opacity-0'
                                              )}
                                            />
                                            <div className="flex flex-col min-w-0">
                                              <span className="font-mono">{ac.tail_number}</span>
                                              {(mfr || mdl) && (
                                                <span className="text-xs text-muted-foreground truncate">
                                                  {[mfr, mdl].filter(Boolean).join(' ')}
                                                </span>
                                              )}
                                            </div>
                                          </CommandItem>
                                        )
                                      })}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {/* Location Combobox */}
                      <FormField
                        control={form.control as any}
                        name="location_id"
                        render={({ field }) => (
                          <FormItem className="flex flex-col">
                            <FormLabel>Location *</FormLabel>
                            <Popover open={locationOpen} onOpenChange={setLocationOpen}>
                              <PopoverTrigger asChild>
                                <FormControl>
                                  <Button
                                    variant="outline"
                                    role="combobox"
                                    aria-expanded={locationOpen}
                                    disabled={isSubmitting || isLocationLocked}
                                    className={cn(
                                      'w-full justify-between font-normal',
                                      !field.value && 'text-muted-foreground'
                                    )}
                                  >
                                    <span className="truncate">
                                      {field.value
                                        ? getLocationDisplay(field.value)
                                        : 'Select location...'}
                                    </span>
                                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                  </Button>
                                </FormControl>
                              </PopoverTrigger>
                              <PopoverContent className="w-[280px] p-0" align="start" onWheel={(e) => e.stopPropagation()}>
                                <Command>
                                  <CommandInput placeholder="Search locations..." />
                                  <CommandList>
                                    <CommandEmpty>No locations found</CommandEmpty>
                                    <CommandGroup>
                                      {locations?.map((loc) => (
                                        <CommandItem
                                          key={loc.id}
                                          value={`${loc.name} ${loc.location_code || ''}`}
                                          onSelect={() => {
                                            field.onChange(loc.id)
                                            setLocationOpen(false)
                                          }}
                                        >
                                          <Check
                                            className={cn(
                                              'mr-2 h-4 w-4',
                                              field.value === loc.id ? 'opacity-100' : 'opacity-0'
                                            )}
                                          />
                                          {loc.name}
                                          {loc.location_code && (
                                            <span className="ml-auto text-xs text-muted-foreground font-mono">
                                              {loc.location_code}
                                            </span>
                                          )}
                                        </CommandItem>
                                      ))}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    {/* Client Combobox */}
                    <FormField
                      control={form.control as any}
                      name="client_id"
                        render={({ field }) => (
                          <FormItem className="flex flex-col">
                            <FormLabel>Client *</FormLabel>
                            <Popover open={clientOpen} onOpenChange={setClientOpen}>
                              <PopoverTrigger asChild>
                                <FormControl>
                                  <Button
                                    variant="outline"
                                    role="combobox"
                                    aria-expanded={clientOpen}
                                    disabled={isSubmitting}
                                    className={cn(
                                      'w-full justify-between font-normal',
                                      !field.value && 'text-muted-foreground'
                                    )}
                                  >
                                    <span className="truncate">
                                      {field.value
                                        ? getClientDisplay(field.value)
                                        : 'Select client...'}
                                    </span>
                                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                  </Button>
                                </FormControl>
                              </PopoverTrigger>
                              <PopoverContent className="w-[280px] p-0" align="start" onWheel={(e) => e.stopPropagation()}>
                                <Command>
                                  <CommandInput placeholder="Search clients..." />
                                  <CommandList>
                                    <CommandEmpty>No clients found</CommandEmpty>
                                    <CommandGroup>
                                      {clients?.map((client) => (
                                        <CommandItem
                                          key={client.id}
                                          value={getClientDisplayName(client)}
                                          onSelect={() => {
                                            field.onChange(client.id)
                                            setClientOpen(false)
                                          }}
                                        >
                                          <Check
                                            className={cn(
                                              'mr-2 h-4 w-4',
                                              field.value === client.id ? 'opacity-100' : 'opacity-0'
                                            )}
                                          />
                                          {getClientDisplayName(client)}
                                        </CommandItem>
                                      ))}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                            <FormMessage />
                          </FormItem>
                        )}
                    />

                    <div className="grid grid-cols-2 gap-4">
                      {/* Lease Contract Select (small list, no search needed) */}
                      <FormField
                        control={form.control as any}
                        name="lease_contract_id"
                        render={({ field }) => (
                          <FormItem className="flex flex-col">
                            <FormLabel>Lease Contract</FormLabel>
                            <Select
                              value={field.value || ''}
                              onValueChange={(v) => {
                                field.onChange(v || null)
                                if (v) form.setValue('transient_stay_id', null)
                              }}
                              disabled={isSubmitting || filteredLeases.length === 0}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue
                                    placeholder={
                                      filteredLeases.length > 0
                                        ? 'Select contract'
                                        : 'No active contracts'
                                    }
                                  />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {filteredLeases.map((lease) => (
                                  <SelectItem key={lease.id} value={lease.id}>
                                    <span className="font-mono">
                                      {lease.contract_number}
                                    </span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {/* Transient Stay Select (mutually exclusive with lease) */}
                      <FormField
                        control={form.control as any}
                        name="transient_stay_id"
                        render={({ field }) => (
                          <FormItem className="flex flex-col">
                            <FormLabel>Transient Stay</FormLabel>
                            <Select
                              value={field.value || ''}
                              onValueChange={(v) => {
                                field.onChange(v || null)
                                if (v) form.setValue('lease_contract_id', null)
                              }}
                              disabled={isSubmitting || filteredTransientStays.length === 0}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue
                                    placeholder={
                                      filteredTransientStays.length > 0
                                        ? 'Select stay'
                                        : 'No active stays'
                                    }
                                  />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {filteredTransientStays.map((stay) => (
                                  <SelectItem key={stay.id} value={stay.id}>
                                    <span className="font-mono">
                                      {stay.booking_number}
                                    </span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  {/* Fueling Details Section */}
                  <Collapsible
                    open={openSections.fueling}
                    onOpenChange={() => toggleSection('fueling')}
                  >
                    <CollapsibleTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        className="flex w-full justify-between p-0 h-auto font-medium"
                      >
                        Fueling Details
                        <ChevronDown
                          className={cn(
                            'h-4 w-4 transition-transform',
                            openSections.fueling && 'rotate-180'
                          )}
                        />
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="space-y-4 pt-4">
                      <FormField
                        control={form.control as any}
                        name="fueled_at"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Date/Time *</FormLabel>
                            <FormControl>
                              <Input
                                type="datetime-local"
                                {...field}
                                disabled={isSubmitting}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={form.control as any}
                          name="fuel_type"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Fuel Type *</FormLabel>
                              <Select
                                value={field.value}
                                onValueChange={field.onChange}
                                disabled={isSubmitting}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {FUEL_TYPES.map((type) => (
                                    <SelectItem key={type} value={type}>
                                      {FUEL_TYPE_LABELS[type]}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control as any}
                          name="pricing_type"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Pricing Type</FormLabel>
                              <Select
                                value={field.value || ''}
                                onValueChange={(v) => field.onChange(v || null)}
                                disabled={isSubmitting}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {FUEL_PRICING_TYPES.map((type) => (
                                    <SelectItem key={type} value={type}>
                                      {FUEL_PRICING_TYPE_LABELS[type]}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={form.control as any}
                          name="quantity_gallons"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Quantity (gal) *</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  step="0.01"
                                  placeholder="0.00"
                                  {...field}
                                  value={field.value || ''}
                                  disabled={isSubmitting}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control as any}
                          name="price_per_gallon"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Price/Gal</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  step="0.0001"
                                  placeholder="0.0000"
                                  {...field}
                                  value={field.value || ''}
                                  disabled={isSubmitting}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Calculated Total Display */}
                      {calculatedTotal > 0 && (
                        <div className="flex items-center justify-between p-3 bg-muted rounded-md">
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Calculator className="h-4 w-4" />
                            <span className="text-sm">Total Amount</span>
                          </div>
                          <span className="font-mono font-bold text-lg">
                            {formatFuelTotal(calculatedTotal)}
                          </span>
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={form.control as any}
                          name="status"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Status *</FormLabel>
                              <Select
                                value={field.value}
                                onValueChange={field.onChange}
                                disabled={isSubmitting}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {FUEL_STATUSES.map((status) => (
                                    <SelectItem key={status} value={status}>
                                      {FUEL_STATUS_LABELS[status]}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control as any}
                          name="source"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Source *</FormLabel>
                              <Select
                                value={field.value}
                                onValueChange={field.onChange}
                                disabled={isSubmitting}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {FUEL_SOURCES.map((source) => (
                                    <SelectItem key={source} value={source}>
                                      {FUEL_SOURCE_LABELS[source]}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </CollapsibleContent>
                  </Collapsible>

                  {/* Operations Section */}
                  <Collapsible
                    open={openSections.operations}
                    onOpenChange={() => toggleSection('operations')}
                  >
                    <CollapsibleTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        className="flex w-full justify-between p-0 h-auto font-medium"
                      >
                        Operations
                        <ChevronDown
                          className={cn(
                            'h-4 w-4 transition-transform',
                            openSections.operations && 'rotate-180'
                          )}
                        />
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="space-y-4 pt-4">
                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={form.control as any}
                          name="truck_id"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Truck ID</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="e.g., T-001"
                                  {...field}
                                  value={field.value || ''}
                                  disabled={isSubmitting}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control as any}
                          name="fueler_name"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Fueler Name</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="Name"
                                  {...field}
                                  value={field.value || ''}
                                  disabled={isSubmitting}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={form.control as any}
                          name="meter_start"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Meter Start</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  step="0.01"
                                  placeholder="Reading"
                                  {...field}
                                  value={field.value ?? ''}
                                  onChange={(e) =>
                                    field.onChange(
                                      e.target.value ? parseFloat(e.target.value) : null
                                    )
                                  }
                                  disabled={isSubmitting}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control as any}
                          name="meter_end"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Meter End</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  step="0.01"
                                  placeholder="Reading"
                                  {...field}
                                  value={field.value ?? ''}
                                  onChange={(e) =>
                                    field.onChange(
                                      e.target.value ? parseFloat(e.target.value) : null
                                    )
                                  }
                                  disabled={isSubmitting}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </CollapsibleContent>
                  </Collapsible>

                  {/* Integration Section */}
                  <Collapsible
                    open={openSections.integration}
                    onOpenChange={() => toggleSection('integration')}
                  >
                    <CollapsibleTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        className="flex w-full justify-between p-0 h-auto font-medium"
                      >
                        Integration
                        <ChevronDown
                          className={cn(
                            'h-4 w-4 transition-transform',
                            openSections.integration && 'rotate-180'
                          )}
                        />
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="space-y-4 pt-4">
                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={form.control as any}
                          name="external_system"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>External System</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="e.g., x1fbo"
                                  {...field}
                                  value={field.value || ''}
                                  disabled={isSubmitting}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control as any}
                          name="external_reference"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>External Reference</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="External ID"
                                  {...field}
                                  value={field.value || ''}
                                  disabled={isSubmitting}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </CollapsibleContent>
                  </Collapsible>

                  {/* Notes Section */}
                  <Collapsible
                    open={openSections.notes}
                    onOpenChange={() => toggleSection('notes')}
                  >
                    <CollapsibleTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        className="flex w-full justify-between p-0 h-auto font-medium"
                      >
                        Notes
                        <ChevronDown
                          className={cn(
                            'h-4 w-4 transition-transform',
                            openSections.notes && 'rotate-180'
                          )}
                        />
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="space-y-4 pt-4">
                      <FormField
                        control={form.control as any}
                        name="notes"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Notes</FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder="Additional notes..."
                                rows={3}
                                {...field}
                                value={field.value || ''}
                                disabled={isSubmitting}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </CollapsibleContent>
                  </Collapsible>
                </form>
              </Form>
            </ScrollArea>
          )}

          <SheetFooter className="flex-row justify-end gap-2 px-4 py-4 border-t bg-background shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="fuel-transaction-form"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? 'Saving...'
                : isEditing
                  ? 'Save Changes'
                  : 'Create Transaction'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Discard Changes Dialog */}
      <AlertDialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to close without
              saving?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continue Editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                form.reset()
                setShowDiscardDialog(false)
                onOpenChange(false)
              }}
            >
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

/**
 * Format ISO date string to datetime-local input format
 */
function formatDateTimeLocal(isoString: string): string {
  const date = new Date(isoString)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${year}-${month}-${day}T${hours}:${minutes}`
}
